import { ActivitiesTestIds } from 'app/pages/loanApplication/tabs/activities/Activities.testIds';
import {
  BORROWING_ENTITY_TABS_HREF,
  ENTITY_TYPE_HREF,
} from 'app/pages/loanApplication/tabs/borrowers/EditEntityModal/EditEntityModal.hrefs';
import { FINANCIAL_INFO_VIEWS_PATHS } from 'app/pages/loanApplication/tabs/financialInfo/FinancialInfo.constants';
import { ClosingFundingDatesFormFieldId } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.constants';
import { SECTION_HREF_MAP as CLOSING_SECTION_HREF_MAP } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/EditDrawer.hrefs';
import { SECTION_HREF_MAP as POST_CLOSING_SECTION_HREF_MAP } from 'app/pages/loanApplication/tabs/postClosing/EditDrawer/EditDrawer.hrefs';
import { PostClosingTestIds } from 'app/pages/loanApplication/tabs/postClosing/PostClosing.testIds';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_SECTION_HREF_MAP,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/EditModal.hrefs';
import { TasksTestIds } from 'app/pages/loanApplication/tabs/tasks/Tasks.testIds';
import { UnderwritingTestIds } from 'app/pages/loanApplication/tabs/underwritingCDS/Underwriting.testIds';
import { AREA_ID_PREFIX } from 'app/services/autoLockForms/autoLockForms';
import {
  ActionsFieldNames,
  ActivitiesSubsectionNames,
  ActivitiesTabNames,
  AutoLockFormsLevelType,
  AutoLockPageCodes,
  BorrowersSubsectionNames,
  BorrowersTabNames,
  ClosingSubsectionNames,
  DenialReasonsTabNames,
  DocumentExpirationDatesTabNames,
  FinancialInfoSubsectionNames,
  GeneralTabNames,
  LoanAndLockInfoTabNames,
  LoanEditorSectionNames,
  NonBorrowersTabNames,
  NotesTabNames,
  PostClosingTabNames,
  PropertyAndLoanSubsectionNames,
  PropertyInfoTabNames,
  TasksSubsectionNames,
  TasksTabNames,
  UnderwritingSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import { loanManagementTestIds } from 'app/shared/components/loanManagement/loanManagement.testIds';

import {
  ActivitiesSubtabRouteParam,
  DocumentsSubtabRouteParam,
  LoanApplicationTabRouteParam,
  TasksSubtabRouteParam,
  UnderwritingSubtabRouteParam,
} from 'constants/loanApplication';

export const VALID_TABS = [
  'property-and-loan',
  'borrowers',
  'financial-information',
  'tasks',
  'underwriting',
  'closing-new',
  'fees',
  'documents',
  'alerts',
  'loan-summary',
];

export const TAB_NAME_TO_ROUTE = {
  'property-and-loan': LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
  borrowers: LoanApplicationTabRouteParam.BORROWERS,
  'financial-information': LoanApplicationTabRouteParam.FINANCIAL_INFO,
  tasks: LoanApplicationTabRouteParam.TASKS,
  underwriting: LoanApplicationTabRouteParam.UNDERWRITING,
  'closing-new': LoanApplicationTabRouteParam.CLOSING_NEW,
  fees: LoanApplicationTabRouteParam.LOAN_FEES,
  documents: LoanApplicationTabRouteParam.DOCUMENTS,
  alerts: LoanApplicationTabRouteParam.ALERTS,
  'loan-summary': LoanApplicationTabRouteParam.LOAN_SUMMARY,
};

export const SUBTAB_ROUTES = {
  tasks: TasksSubtabRouteParam,
  underwriting: UnderwritingSubtabRouteParam,
  documents: DocumentsSubtabRouteParam,
};

// Query contract of the Suppressed fields deep link (DEV-5146):
// `?view=suppressed[&item=<checklistItemId>]` on the Alerts tab route.
export const SUPPRESSED_FIELDS_QUERY = {
  VIEW_KEY: 'view',
  VIEW_VALUE: 'suppressed',
  ITEM_KEY: 'item',
};

// The appFormsTree names its Property & Loan levels in camelCase, but the edit-modal
// ROUTES use the kebab hrefs from EditModal.constants — navigating with the raw tree
// names changes the URL without matching any route, so the modal never opens (DEV-5218).
export const PROPERTY_AND_LOAN_SUBSECTION_TO_HREF = {
  [PropertyAndLoanSubsectionNames.PROPERTY_INFO]: ENTITY_TYPE_HREF_MAP.PROPERTY_INFO,
  [PropertyAndLoanSubsectionNames.LOAN_AND_LOCK_INFO]: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
  [PropertyAndLoanSubsectionNames.CONTACTS]: ENTITY_TYPE_HREF_MAP.CONTACTS,
};

// The tree still files the loan's contacts under Property & Loan, but their edit drawer opens on
// the Contacts tab (LEN2-798).
export const PROPERTY_AND_LOAN_SUBSECTION_TO_TAB_ROUTE = {
  [PropertyAndLoanSubsectionNames.CONTACTS]: LoanApplicationTabRouteParam.CONTACTS,
};

export const PROPERTY_AND_LOAN_TAB_TO_SECTION_HREF = {
  [LoanAndLockInfoTabNames.LOAN_INFO]: LOAN_AND_LOCK_SECTION_HREF_MAP.LOAN_INFO,
  [LoanAndLockInfoTabNames.LOCK_INFO]: LOAN_AND_LOCK_SECTION_HREF_MAP.LOCK_INFO,
  [LoanAndLockInfoTabNames.ADDITIONAL_FINANCING]:
    LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING,
  [LoanAndLockInfoTabNames.CREDITS]: LOAN_AND_LOCK_SECTION_HREF_MAP.CREDITS,
  [PropertyInfoTabNames.PROPERTY]: PROPERTY_SECTION_HREF_MAP.PROPERTY,
  [PropertyInfoTabNames.APPRAISAL]: PROPERTY_SECTION_HREF_MAP.APPRAISAL,
  [PropertyInfoTabNames.PROPERTY_EXPENSES]: PROPERTY_SECTION_HREF_MAP.PROPERTY_EXPENSES,
  [PropertyInfoTabNames.PROJECT]: PROPERTY_SECTION_HREF_MAP.PROJECT,
  [PropertyInfoTabNames.TRANSACTION_DETAILS]: PROPERTY_SECTION_HREF_MAP.TRANSACTION_DETAILS,
  [PropertyInfoTabNames.AGENT_AND_SELLER]: PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER,
};

// The Closing edit drawer opens on `closing-new/<sectionHref>`, and its sections do not
// line up with the appFormsTree's Closing subsections — navigating with the raw tree
// names lands on the read-only Closing summary instead of the editor (LEN2-499).
// Post Closing has no section in this drawer, so it stays unmapped.
export const CLOSING_SUBSECTION_TO_SECTION_HREF = {
  [ClosingSubsectionNames.CLOSING_INFORMATION]: CLOSING_SECTION_HREF_MAP.SCHEDULING,
  [ClosingSubsectionNames.CLOSING_COMPANIES]: CLOSING_SECTION_HREF_MAP.TITLE_COMPANY,
  [ClosingSubsectionNames.TITLE_HOLDER]: CLOSING_SECTION_HREF_MAP.TITLE_HOLDER,
  [ClosingSubsectionNames.FUNDING_INFORMATION]: CLOSING_SECTION_HREF_MAP.FUNDING_INFORMATION,
};

// The tree files Post Closing under the Closing section, but it is edited in the Post
// Closing tab's own drawer (`post-closing/<sectionHref>`), not the Closing one (LEN2-499).
export const POST_CLOSING_TAB_TO_SECTION_HREF = {
  [PostClosingTabNames.SHIPPING_INFORMATION]: POST_CLOSING_SECTION_HREF_MAP.SHIPPING,
  [PostClosingTabNames.INVESTOR]: POST_CLOSING_SECTION_HREF_MAP.INVESTOR,
  [PostClosingTabNames.ACCOUNTING_INFORMATION]: POST_CLOSING_SECTION_HREF_MAP.ACCOUNTING,
  [PostClosingTabNames.SERVICER_INFORMATION]: POST_CLOSING_SECTION_HREF_MAP.SERVICER,
  [PostClosingTabNames.MERS_INFORMATION]: POST_CLOSING_SECTION_HREF_MAP.MERS,
  [PostClosingTabNames.PAYMENT_INFORMATION]: POST_CLOSING_SECTION_HREF_MAP.PAYMENT,
  [PostClosingTabNames.FINAL_DOCUMENTS]: POST_CLOSING_SECTION_HREF_MAP.FINAL_DOCUMENTS,
  [PostClosingTabNames.SHIPPER_MILESTONE]: POST_CLOSING_SECTION_HREF_MAP.SHIPPER_MILESTONE,
  [PostClosingTabNames.FLIPPED_UNSALABLE]: POST_CLOSING_SECTION_HREF_MAP.FLIPPED_UNSALABLE,
};

// The tree's single Closing Information form spans two drawer sections: its note,
// funding, disbursement, payment and approval dates live in Closing & Funding Dates.
export const CLOSING_FUNDING_DATES_FIELD_NAMES = Object.values(ClosingFundingDatesFormFieldId);

// Editor screens the appFormsTree has no area for (they hold no tree fields), linked
// by a `tab:`-shaped ref so they read as section links everywhere a `fieldRef` is
// discriminated; "Go to field" opens their editor drawer directly (LEN2-499).
const LOAN_AND_LOCK_INFO_AREA_PATH = [
  LoanEditorSectionNames.PROPERTY_AND_LOAN,
  PropertyAndLoanSubsectionNames.LOAN_AND_LOCK_INFO,
].join('-');

const EditorLinkTargetRef = {
  PROGRAMS_PRICING: [
    `${AREA_ID_PREFIX[AutoLockFormsLevelType.TAB]}${LOAN_AND_LOCK_INFO_AREA_PATH}`,
    LoanAndLockInfoTabNames.PROGRAMS_PRICING,
  ].join('-'),
};

const LOAN_AND_LOCK_INFO_AREA_ID = `${
  AREA_ID_PREFIX[AutoLockFormsLevelType.SUBSECTION]
}${LOAN_AND_LOCK_INFO_AREA_PATH}`;

export const EDITOR_LINK_TARGETS = {
  [EditorLinkTargetRef.PROGRAMS_PRICING]: {
    label: 'Programs & Pricing',
    parentAreaId: LOAN_AND_LOCK_INFO_AREA_ID,
    tab: LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
    subtab: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
    // The Property & Loan edit modal reads its drawer section from the `entityId` param.
    entityId: LOAN_AND_LOCK_SECTION_HREF_MAP.PROGRAMS_PRICING,
  },
};

// The Actions page's Program Change "field" is the program selection itself, so a link to
// it opens Programs & Pricing (LEN2-499).
const PROGRAM_CHANGE_FIELD_ID = [
  AutoLockPageCodes.ACTIONS, // section
  AutoLockPageCodes.ACTIONS, // subsection
  AutoLockPageCodes.ACTIONS, // tab
  AutoLockPageCodes.ACTIONS, // form
  ActionsFieldNames.PROGRAM_CHANGE,
].join('-');

export const FIELD_TO_EDITOR_LINK_TARGET = {
  [PROGRAM_CHANGE_FIELD_ID]: EDITOR_LINK_TARGETS[EditorLinkTargetRef.PROGRAMS_PRICING],
};

export const VALID_BORROWER_SECTIONS = Object.values(BORROWING_ENTITY_TABS_HREF);

export const VALID_FINANCIAL_VIEWS = Object.values(FINANCIAL_INFO_VIEWS_PATHS);

export const ENTITY_TYPE_TO_TAB = {
  task: 'tasks',
  document: 'documents',
  condition: 'underwriting',
  income: 'financial-information',
  asset: 'financial-information',
  liability: 'financial-information',
  reo: 'financial-information',
};

export const ENTITY_TYPE_TO_FINANCIAL_VIEW = {
  income: FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT,
  asset: FINANCIAL_INFO_VIEWS_PATHS.ASSETS,
  liability: FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES,
  reo: FINANCIAL_INFO_VIEWS_PATHS.REO,
};

// Maps a Financial Info SUBSECTION name (the appFormsTree area's `subsection`) to
// its dedicated financial-view subtab path — used to expand a linked section when
// navigating to it (DEV-4467).
export const FINANCIAL_SUBSECTION_TO_VIEW = {
  [FinancialInfoSubsectionNames.EMPLOYMENT_INFO]: FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT,
  [FinancialInfoSubsectionNames.LIABILITIES]: FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES,
  [FinancialInfoSubsectionNames.REO]: FINANCIAL_INFO_VIEWS_PATHS.REO,
  [FinancialInfoSubsectionNames.ASSETS]: FINANCIAL_INFO_VIEWS_PATHS.ASSETS,
};

// DOM attribute marking a loan-editor section container so a "Go to section"
// navigation can scroll it into view (DEV-4467).
export const SECTION_CONTAINER_ATTR = 'data-section-id';

export const SECTION_TO_TAB = {
  propertyAndLoan: LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
  borrowers: LoanApplicationTabRouteParam.BORROWERS,
  financialInfo: LoanApplicationTabRouteParam.FINANCIAL_INFO,
  underwriting: LoanApplicationTabRouteParam.UNDERWRITING,
  closing: LoanApplicationTabRouteParam.CLOSING_NEW,
  fees: LoanApplicationTabRouteParam.LOAN_FEES,
  [LoanEditorSectionNames.ACTIVITIES]: LoanApplicationTabRouteParam.ACTIVITIES,
  [LoanEditorSectionNames.TASKS]: LoanApplicationTabRouteParam.TASKS,
};

// Tab routes resolved by SUBSECTION, ahead of SECTION_TO_TAB: Post Closing sits under the
// Closing section in the appFormsTree but has its own tab (LEN2-499).
export const SUBSECTION_TO_TAB = {
  [ClosingSubsectionNames.POST_CLOSING]: LoanApplicationTabRouteParam.POST_CLOSING,
};

// Lead details are edited only while creating/converting a lead — no loan screen holds
// them, so they are neither linkable nor navigable from a loan (LEN2-499).
export const LOAN_UNREACHABLE_SECTIONS = [LoanEditorSectionNames.LEAD];

export const getInPageEditorKey = (subsection, tab) => `${subsection}-${tab}`;

const getTestIdSelector = testId => `[data-testid="${testId}"]`;

// MenuPopoverCDS renders its items as plain buttons inside the MUI popover.
const MENU_POPOVER_ITEM_SELECTOR = '.MuiPopover-root button';

// Areas edited in place on a loan page (or in a dialog the page opens) rather than in a
// routed drawer, keyed by `${subsection}-${tab}`: the route showing them, the `openers`
// that open the editor (alternatives tried in order — the first whose first step is
// clickable runs; each step clicks an enabled element matching `selector`, and `text`
// when set). Fields whose DOM name is not the tree's carry `data-field-id` instead
// (see autoLockForms.fieldIds.js).
// Areas edited per row (task info, call notes) have no single editor to open, so they
// land on the list holding them (LEN2-499).
export const IN_PAGE_EDITORS = {
  [getInPageEditorKey(UnderwritingSubsectionNames.GENERAL, GeneralTabNames.GENERAL)]: {
    tabRoute: LoanApplicationTabRouteParam.UNDERWRITING,
    openers: [
      [
        {
          selector: `${getTestIdSelector(
            UnderwritingTestIds.GENERAL_FORM_LOAN_EXPIRATION_DATE_DATA_BUTTON,
          )} ${getTestIdSelector(loanManagementTestIds.getWrapperActionButtonId(0))}`,
        },
      ],
    ],
  },
  [getInPageEditorKey(UnderwritingSubsectionNames.NOTES, NotesTabNames.NOTES)]: {
    tabRoute: LoanApplicationTabRouteParam.UNDERWRITING,
    openers: [
      [{ selector: getTestIdSelector(UnderwritingTestIds.NOTES_ADD_BUTTON) }],
      [
        { selector: getTestIdSelector(UnderwritingTestIds.NOTES_ACTION_BUTTON) },
        { selector: MENU_POPOVER_ITEM_SELECTOR, text: 'Edit' },
      ],
    ],
  },
  [getInPageEditorKey(
    UnderwritingSubsectionNames.DOCUMENT_EXPIRATION_DATES,
    DocumentExpirationDatesTabNames.DOCUMENT_EXPIRATION_DATES,
  )]: {
    tabRoute: LoanApplicationTabRouteParam.UNDERWRITING,
    openers: [[{ selector: getTestIdSelector(UnderwritingTestIds.DOC_EXPIRATION_ADD_BUTTON) }]],
  },
  [getInPageEditorKey(
    UnderwritingSubsectionNames.DENIAL_REASONS,
    DenialReasonsTabNames.DENIAL_REASONS,
  )]: {
    tabRoute: LoanApplicationTabRouteParam.UNDERWRITING,
    openers: [[{ selector: getTestIdSelector(UnderwritingTestIds.LOAN_DENIAL_ADD_BUTTON) }]],
  },
  [getInPageEditorKey(ClosingSubsectionNames.POST_CLOSING, PostClosingTabNames.DOCUMENTS_LIST)]: {
    tabRoute: LoanApplicationTabRouteParam.POST_CLOSING,
    openers: [[{ selector: getTestIdSelector(PostClosingTestIds.ADD_DOCUMENT_BUTTON) }]],
  },
  [getInPageEditorKey(ActivitiesSubsectionNames.ACTIVITIES, ActivitiesTabNames.EMAILS)]: {
    tabRoute: LoanApplicationTabRouteParam.ACTIVITIES,
    subtab: ActivitiesSubtabRouteParam.EMAILS,
    openers: [[{ selector: getTestIdSelector(ActivitiesTestIds.CREATE_EMAIL_BUTTON) }]],
  },
  [getInPageEditorKey(ActivitiesSubsectionNames.ACTIVITIES, ActivitiesTabNames.CALLS)]: {
    tabRoute: LoanApplicationTabRouteParam.ACTIVITIES,
    subtab: ActivitiesSubtabRouteParam.CALLS,
  },
  [getInPageEditorKey(TasksSubsectionNames.TASKS, TasksTabNames.ASSIGN_TASK)]: {
    tabRoute: LoanApplicationTabRouteParam.TASKS,
    subtab: TasksSubtabRouteParam.PENDING,
    openers: [[{ selector: getTestIdSelector(TasksTestIds.ASSIGN_TASK_BUTTON) }]],
  },
  [getInPageEditorKey(TasksSubsectionNames.TASKS, TasksTabNames.TASK_INFORMATION)]: {
    tabRoute: LoanApplicationTabRouteParam.TASKS,
    subtab: TasksSubtabRouteParam.PENDING,
  },
  // The Borrowers page opens the NBE drawer itself when the loan has a single NBE
  // (useSingleNbeDrawerRedirect).
  [getInPageEditorKey(BorrowersSubsectionNames.NON_BORROWERS, NonBorrowersTabNames.NBE_INFO)]: {
    tabRoute: LoanApplicationTabRouteParam.BORROWERS,
    subtab: ENTITY_TYPE_HREF.NON_BORROWER,
  },
};

export const BORROWERS_SECTION_NAME = 'borrowers';

export const BORROWER_ENTITY_TYPE_HREF = ENTITY_TYPE_HREF.BORROWER;

export const BORROWER_FORM_TAB_TO_SECTION_HREF = {
  [BorrowersTabNames.BORROWER]: BORROWING_ENTITY_TABS_HREF.BORROWER,
  [BorrowersTabNames.ADDRESS]: BORROWING_ENTITY_TABS_HREF.ADDRESS,
  [BorrowersTabNames.CREDIT_REPORTING]: BORROWING_ENTITY_TABS_HREF.CREDIT_REPORTING,
  [BorrowersTabNames.ALTERNATE_NAMES]: BORROWING_ENTITY_TABS_HREF.ALTERNATE_NAMES,
  [BorrowersTabNames.POA]: BORROWING_ENTITY_TABS_HREF.POA,
  [BorrowersTabNames.DECLARATIONS]: BORROWING_ENTITY_TABS_HREF.DECLARATIONS,
};

export const FIELD_HIGHLIGHT_DURATION_MS = 3000;
export const FIELD_HIGHLIGHT_SCROLL_SETTLE_MS = 600;
export const FIELD_HIGHLIGHT_CLASS = 'loan-field-highlight';

export const MAX_WAIT_ATTEMPTS = 20;
export const WAIT_INTERVAL_MS = 100;

export const FIELD_HIGHLIGHT_MAX_ATTEMPTS = 50;
export const FIELD_HIGHLIGHT_INTERVAL_MS = 100;
// An in-page editor's opener only turns clickable once its page has loaded (e.g. Underwriting's
// Add buttons stay disabled for several seconds), so it gets a longer wait than the field
// highlight (15s at FIELD_HIGHLIGHT_INTERVAL_MS) (LEN2-499).
export const IN_PAGE_EDITOR_OPENER_MAX_ATTEMPTS = 150;
export const FIELD_HIGHLIGHT_DOM_SETTLE_MS = 250;

export const NAV_DIAG_ATTEMPT_LOG_INTERVAL = 10;
export const NAV_DIAG_PROBE_SAMPLE_SIZE = 10;
export const NAV_DIAG_WINDOW_KEY = 'aiChatNavDiagnostics';
